import {
	AddressElement,
	PaymentElement,
	useElements,
	useStripe
} from '@stripe/react-stripe-js'
import { StripePaymentElementOptions } from '@stripe/stripe-js'
import { useSignal } from '@preact/signals'

import { Stepper } from 'pages/settings/teams/components/Stepper'
import { userInformationState } from 'state'
import { Button, HorizontalDivider, TextLink, Typography } from 'components'
import { config } from 'config'

import { usePayments } from '../../contexts/PaymentsContext'
import {
	BillingCycleSelector,
	SeatsCounter,
	SelectedPlanCard
} from './components'

export const PaymentDetails = () => {
	const stripe = useStripe()
	const elements = useElements()

	const message = useSignal('')
	const isLoading = useSignal(false)
	const { returnUrl } = usePayments()

	const handleSubmit = async (e: { preventDefault: () => void }) => {
		e.preventDefault()

		if (!stripe || !elements) {
			// Stripe.js hasn't yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return
		}

		isLoading.value = true

		let redirectPayments = `${returnUrl}/teams/setup-members?current_plan=${
			userInformationState.peek()?.subscription.currentPlan.id
		}`

		if (config.REDIRECT_PAYMENTS_TO_LOCALHOST) {
			redirectPayments = `https://local.codigohub.dev:3000/teams/setup-members?current_plan=${
				userInformationState.peek()?.subscription.currentPlan.id
			}`
		}

		const { error } = await stripe.confirmPayment({
			elements,
			confirmParams: {
				return_url: redirectPayments
			}
		})

		// This point will only be reached if there is an immediate error when
		// confirming the payment. Otherwise, your customer will be redirected to
		// your `return_url`. For some payment methods like iDEAL, your customer will
		// be redirected to an intermediate site first to authorize the payment, then
		// redirected to the `return_url`.
		if (error.type === 'card_error' || error.type === 'validation_error') {
			message.value = error.message || 'An error occurred. Please try again.'
		} else {
			message.value = 'An unexpected error occurred.'
		}

		isLoading.value = false
	}

	const paymentElementOptions: StripePaymentElementOptions = {
		layout: 'accordion',
		fields: {
			billingDetails: {
				address: 'never'
			}
		}
	}

	return (
		<div class='flex-1'>
			<div class='flex grid-cols-1 flex-col-reverse gap-10 xl:grid xl:grid-cols-[2fr_1fr]'>
				<div>
					<Typography
						color='text-black-100'
						text='Payment Details'
						variant='h3'
					/>
					<Typography
						class='mt-2'
						color='text-black-200'
						text='Define your seats and your Payment Method'
						variant='text1'
					/>
				</div>
				<Stepper
					steps={[
						{ number: 1, completed: true, current: false },
						{ number: 2, completed: false, current: true },
						{ number: 3, completed: false, current: false }
					]}
				/>
			</div>

			<HorizontalDivider className='my-16 xl:my-10' />

			<div class='flex grid-cols-1 flex-col-reverse gap-10 xl:grid xl:grid-cols-[2fr_1fr]'>
				<div>
					<div class='grid grid-cols-1 gap-8 lg:grid-cols-2'>
						<BillingCycleSelector />
						<SeatsCounter />
					</div>

					<HorizontalDivider className='my-16 xl:my-10' />

					<div class='mt-10 space-y-10 xl:mt-5 xl:space-y-4'>
						<form id='payment-form' onSubmit={handleSubmit}>
							<Typography
								class='mb-4'
								text='Invoice Information'
								variant='h4'
								weight='font-semibold'
							/>

							<AddressElement
								options={{
									mode: 'billing',
									display: { name: 'split' },
									defaultValues: {
										// todo: fix the default values
										lastName: userInformationState.peek()?.lastName,
										firstName: userInformationState.peek()?.firstName
									}
								}}
							/>

							<HorizontalDivider className='my-16 xl:my-10' />

							<Typography
								class='mb-4'
								text='Payment Method'
								variant='h4'
								weight='font-semibold'
							/>

							<PaymentElement
								id='payment-element'
								options={paymentElementOptions}
							/>
						</form>
					</div>

					<HorizontalDivider className='my-16 xl:my-10' />

					<div class='mb-10 mt-6 flex flex-col-reverse gap-4 md:flex-row md:justify-between xl:mb-0'>
						<TextLink
							ariaLabel='Back'
							href='/setup-team'
							text='Back'
							variant='secondary'
						/>
						<Button
							className='mb-5 md:mb-0'
							disabled={isLoading.value || !stripe || !elements}
							id='submit'
							isLoading={isLoading.value}
							onClick={handleSubmit}
							text='Acquire Seats'
						/>
					</div>
				</div>

				<SelectedPlanCard />
			</div>
		</div>
	)
}
